export const WHY_INSTITUTE = [
  { title: "Executive Education", text: "Structured, rigorous learning designed for people who carry real responsibility — not entry-level training repackaged for executives." },
  { title: "Applied Learning", text: "Every concept is translated into tools, decisions and practice. Learning is built to be used, not filed away." },
  { title: "Business Research", text: "Research that examines how organisations actually compete, decide, lead and grow — feeding directly into what we teach." },
  { title: "Strategic Thinking", text: "Proprietary frameworks that sharpen judgement and the quality of thinking, not just the volume of knowledge." },
  { title: "Leadership Development", text: "Building the capacity to lead people, performance and change — at every level of the organisation." },
  { title: "Intellectual Capital", text: "Turning experience and insight into frameworks and assets that leaders and organisations can keep, reuse and compound." },
];

export const AUDIENCES = [
  { title: "Entrepreneurs", text: "Build the thinking, discipline and commercial judgement to turn ambition into an enduring business." },
  { title: "Founders", text: "Move from doing everything yourself to leading an organisation that can scale beyond you." },
  { title: "Business Leaders", text: "Sharpen strategy, executive judgement and enterprise-wide leadership capability." },
  { title: "Managers", text: "Develop the effectiveness to turn plans into consistent performance through people." },
  { title: "Sales Professionals", text: "Master commercial thinking, customer value creation and disciplined execution." },
  { title: "HR / L&D Leaders", text: "Design capability systems that genuinely move business outcomes, not attendance numbers." },
  { title: "Young Professionals", text: "Accelerate your growth with structured thinking and sound business fundamentals." },
  { title: "Organisations", text: "Develop leadership, strategic and commercial capability at team and enterprise level.", to: "/organisations" },
];

export const DOMAINS = [
  { title: "Strategy", text: "Direction, choices, competitive advantage and long-term value creation." },
  { title: "Leadership", text: "Leading self, teams and organisations through performance and change." },
  { title: "Sales & Commercial Excellence", text: "Commercial thinking, customer value and revenue discipline." },
  { title: "Business & Entrepreneurship", text: "Building, funding and growing ventures with sound fundamentals." },
  { title: "Operations & Execution", text: "Turning strategy into reliable delivery, process and performance." },
  { title: "Digital & AI", text: "Executive fluency in digital transformation and applied AI." },
  { title: "Customer", text: "Customer centricity, experience and enduring relationships." },
  { title: "People & Organisation", text: "Culture, talent, structure and organisational effectiveness." },
];

export const PILLARS = [
  { title: "Perspective", text: "We help leaders see their business the way it actually is — and the way it could be." },
  { title: "Rigour", text: "Every framework is tested against evidence, research and real operating conditions." },
  { title: "Relevance", text: "Content is built for the decisions leaders face now — not for theory alone." },
  { title: "Application", text: "Learning is designed to be used on Monday morning, not admired on a slide." },
];

export const ORG_CAPABILITIES = [
  "Leadership Development",
  "Strategic Thinking",
  "Manager Effectiveness",
  "Sales Excellence",
  "Customer Centricity",
  "AI & Digital Capability",
  "Transformation Capability",
];

export const KNOWLEDGE_CATEGORIES = [
  { title: "Executive Insights", text: "Short, sharp thinking for leaders — on strategy, leadership, customers and performance.", status: "Now publishing" },
  { title: "Frameworks", text: "Practical thinking tools from the Vantage Thinking Toolbox™, with guidance on when and how to use them.", status: "Thirteen frameworks published" },
  { title: "Research", text: "Applied business research examining how organisations compete, decide and grow.", status: "Research agenda in development" },
  { title: "Case Studies", text: "Documented applications of Vantage frameworks in real business situations.", status: "To be published as engagements conclude" },
  { title: "Videos", text: "Executive briefings and framework walk-throughs.", status: "Forthcoming" },
  { title: "Podcasts", text: "Conversations on leadership, strategy and building capability.", status: "Forthcoming" },
  { title: "Webinars", text: "Live and recorded sessions with Vantage faculty and practitioners.", status: "Forthcoming" },
  { title: "Toolkits", text: "Downloadable worksheets, canvases and practical templates.", status: "First toolkit in preparation" },
];

export const PHILOSOPHY_CHAIN = [
  "Experience", "Knowledge", "Research", "Frameworks", "Capability", "Application", "Business Impact",
];

export const HOME_FAQS = [
  {
    q: "What is The Vantage Institute?",
    a: "The Vantage Institute is an executive education and research institution. It develops leadership capability, strategic thinking, business skills and practical intellectual capital for leaders, professionals, entrepreneurs and organisations. It is a Vantage Group™ institution and an Executive Education & Research Division of Wowrakesh Business Academy®.",
  },
  {
    q: "Who is The Vantage Institute for?",
    a: "The Institute serves entrepreneurs, founders, business leaders, managers, sales professionals, HR and L&D leaders, young professionals, and organisations that want to build capability at team or enterprise level.",
  },
  {
    q: "What does The Vantage Institute teach?",
    a: "The Institute teaches across eight capability domains: Strategy, Leadership, Sales & Commercial Excellence, Business & Entrepreneurship, Operations & Execution, Digital & AI, Customer, and People & Organisation — grounded in the proprietary Vantage Thinking Toolbox™.",
  },
  {
    q: "What is the Vantage Thinking Toolbox™?",
    a: "The Vantage Thinking Toolbox™ is the Institute's proprietary thinking platform: a structured set of thirteen thinking frameworks — from First Principles and Strategic Thinking to Executive and Future Thinking — designed to improve the quality of judgement behind business decisions.",
  },
  {
    q: "What programmes does Vantage offer?",
    a: "The catalogue spans masterclasses, executive programmes, certification programmes, corporate programmes, workshops, micro-learning and structured learning journeys. Programmes can be explored by capability, audience, format and duration on the Programmes page.",
  },
];

export const ABOUT_FAQS = [
  {
    q: "What is executive education?",
    a: "Executive education is structured learning designed for people who already carry responsibility — leaders, managers and founders. Unlike academic study, it concentrates on judgement, decision quality and application: capability that changes how a leader performs, not just what they know.",
  },
  {
    q: "What is executive capability development?",
    a: "Executive capability development is the deliberate building of the thinking, skills and judgement a leader needs for their next stage of responsibility — strategic thinking, commercial acumen, leading people and making sound decisions under uncertainty.",
  },
  {
    q: "How is The Vantage Institute related to The Vantage Group™?",
    a: "The Vantage Group™ advises leaders and enterprises. The Vantage Institute™ develops the thinking, knowledge, capabilities and intellectual capital that make that advice possible. Advisory identifies what must change; the Institute builds the human capability to carry that change through.",
  },
  {
    q: "How is The Vantage Institute related to Wowrakesh Business Academy®?",
    a: "The Vantage Institute operates as an Executive Education & Research Division of Wowrakesh Business Academy®, combining the Academy's educational foundation with the Institute's research and executive development mandate.",
  },
];

export const ORG_FAQS = [
  {
    q: "How does Vantage work with organisations?",
    a: "Engagements begin with a corporate conversation to understand the business context, the capability gap and the outcome that matters. The Institute then designs a solution — a programme, learning journey or workshop series — built around application and measurable behaviour change.",
  },
  {
    q: "What capability areas does Vantage cover for organisations?",
    a: "Leadership development, strategic thinking, manager effectiveness, sales excellence, customer centricity, AI and digital capability, and transformation capability.",
  },
  {
    q: "Can programmes be customised for our organisation?",
    a: "Yes. Corporate programmes are designed around the organisation's context, audience and desired outcomes. The enquiry form on this page is the starting point for that conversation.",
  },
];
